import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const RDCalculatorScreen = () => {
  const { colors } = useTheme();
  const [monthlyDeposit, setMonthlyDeposit] = useState('5000');
  const [interestRate, setInterestRate] = useState(6.5);
  const [tenure, setTenure] = useState(12);
  const [tenureType, setTenureType] = useState('months');
  const [selectedBank, setSelectedBank] = useState('sbi');
  const [isSeniorCitizen, setIsSeniorCitizen] = useState(false);
  const [taxRate, setTaxRate] = useState(30);
  const [depositFrequency, setDepositFrequency] = useState('monthly');
  const [showComparison, setShowComparison] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const banks = [
    {
      name: 'State Bank of India',
      code: 'sbi',
      rates: {
        '6 months': 5.5,
        '1 year': 6.0,
        '2 years': 6.25,
        '3 years': 6.5,
        '5 years': 6.25,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'HDFC Bank',
      code: 'hdfc',
      rates: {
        '6 months': 6.0,
        '1 year': 6.5,
        '2 years': 6.75,
        '3 years': 7.0,
        '5 years': 6.75,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'ICICI Bank',
      code: 'icici',
      rates: {
        '6 months': 5.75,
        '1 year': 6.25,
        '2 years': 6.5,
        '3 years': 6.75,
        '5 years': 6.5,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'Axis Bank',
      code: 'axis',
      rates: {
        '6 months': 6.25,
        '1 year': 6.75,
        '2 years': 7.0,
        '3 years': 7.25,
        '5 years': 7.0,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'Punjab National Bank',
      code: 'pnb',
      rates: {
        '6 months': 5.25,
        '1 year': 5.75,
        '2 years': 6.0,
        '3 years': 6.25,
        '5 years': 6.0,
      },
      seniorBonus: 0.5,
    },
  ];

  const frequencyOptions = [
    { label: 'Monthly', value: 'monthly', multiplier: 12 },
    { label: 'Quarterly', value: 'quarterly', multiplier: 4 },
    { label: 'Half-Yearly', value: 'half-yearly', multiplier: 2 },
    { label: 'Yearly', value: 'yearly', multiplier: 1 },
  ];

  const tenureOptions = [
    { label: 'Months', value: 'months' },
    { label: 'Years', value: 'years' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const getBankRate = (bankCode, tenureInMonths) => {
    const bank = banks.find(b => b.code === bankCode);
    if (!bank) return 6.0;

    let rate = 6.0;
    
    if (tenureInMonths <= 6) rate = bank.rates['6 months'] || 5.5;
    else if (tenureInMonths <= 12) rate = bank.rates['1 year'] || 6.0;
    else if (tenureInMonths <= 24) rate = bank.rates['2 years'] || 6.25;
    else if (tenureInMonths <= 36) rate = bank.rates['3 years'] || 6.5;
    else rate = bank.rates['5 years'] || 6.25;

    // Add senior citizen bonus
    if (isSeniorCitizen) {
      rate += bank.seniorBonus;
    }

    return rate;
  };

  const calculateRD = () => {
    const monthlyAmount = parseFloat(monthlyDeposit) || 0;
    let tenureInMonths = parseFloat(tenure) || 0;
    
    // Convert tenure to months
    if (tenureType === 'years') {
      tenureInMonths = tenureInMonths * 12;
    }

    // Get frequency multiplier
    const frequency = frequencyOptions.find(f => f.value === depositFrequency);
    const depositsPerYear = frequency.multiplier;
    const depositAmount = depositFrequency === 'monthly' ? monthlyAmount : 
                         depositFrequency === 'quarterly' ? monthlyAmount * 3 :
                         depositFrequency === 'half-yearly' ? monthlyAmount * 6 :
                         monthlyAmount * 12;

    // Get bank-specific rate or use manual rate
    const rate = selectedBank ? getBankRate(selectedBank, tenureInMonths) : interestRate;
    
    // Calculate RD maturity using the standard RD formula
    // M = P * [(1 + r/n)^(n*t) - 1] / (r/n) * (1 + r/n)
    const r = rate / 100;
    const n = depositsPerYear; // compounding frequency equals deposit frequency
    const t = tenureInMonths / 12; // tenure in years
    
    let maturityAmount = 0;
    let totalDeposits = 0;
    
    // Calculate for each deposit installment
    for (let i = 0; i < tenureInMonths; i++) {
      if (i % (12 / depositsPerYear) === 0) { // Deposit made
        const remainingMonths = tenureInMonths - i;
        const remainingYears = remainingMonths / 12;
        const installmentMaturity = depositAmount * Math.pow(1 + r / n, n * remainingYears);
        maturityAmount += installmentMaturity;
        totalDeposits += depositAmount;
      }
    }

    const interestEarned = maturityAmount - totalDeposits;

    // Calculate TDS (Tax Deducted at Source)
    const tdsThreshold = 40000; // TDS applicable if interest > 40,000
    const tdsRate = 0.1; // 10% TDS
    let tdsAmount = 0;
    
    if (interestEarned > tdsThreshold) {
      tdsAmount = interestEarned * tdsRate;
    }

    // Calculate tax on interest (as per income tax slab)
    const taxOnInterest = interestEarned * (taxRate / 100);
    const postTaxInterest = interestEarned - taxOnInterest;
    const postTaxMaturity = totalDeposits + postTaxInterest;

    // Calculate effective rate
    const effectiveRate = ((maturityAmount / totalDeposits) ** (1 / t) - 1) * 100;

    // Calculate monthly breakdown for visualization
    const monthlyBreakdown = [];
    let cumulativeDeposits = 0;
    let cumulativeMaturity = 0;
    
    for (let month = 1; month <= Math.min(tenureInMonths, 60); month++) {
      if ((month - 1) % (12 / depositsPerYear) === 0) {
        cumulativeDeposits += depositAmount;
      }
      
      // Calculate maturity for deposits made so far
      let monthlyMaturity = 0;
      for (let i = 0; i < month; i++) {
        if (i % (12 / depositsPerYear) === 0) {
          const remainingMonths = month - i;
          const remainingYears = remainingMonths / 12;
          monthlyMaturity += depositAmount * Math.pow(1 + r / n, n * remainingYears);
        }
      }
      
      monthlyBreakdown.push({
        month,
        deposits: cumulativeDeposits,
        maturity: monthlyMaturity,
        interest: monthlyMaturity - cumulativeDeposits,
      });
    }

    return {
      monthlyAmount: depositAmount,
      totalDeposits,
      maturityAmount,
      interestEarned,
      rate,
      effectiveRate,
      tdsAmount,
      taxOnInterest,
      postTaxInterest,
      postTaxMaturity,
      tenureInMonths,
      depositsPerYear,
      monthlyBreakdown,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateRD();
    setResults(calculatedResults);
  }, [
    monthlyDeposit, interestRate, tenure, tenureType, depositFrequency,
    selectedBank, isSeniorCitizen, taxRate
  ]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Recurring Deposit Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate RD returns with bank-specific rates, flexible deposit frequency, and tax implications
      </Text>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to Recurring Deposit Calculator</Text>
      <Text style={styles.seoSubtitle}>Master RD Planning with Advanced Features & Bank Comparisons</Text>
      
      <Text style={styles.seoText}>
        Our comprehensive Recurring Deposit Calculator helps you plan systematic savings with precision. 
        Compare bank-specific interest rates, understand tax implications, and optimize your RD investments 
        for maximum returns. Whether you're planning for short-term goals or long-term wealth creation, 
        this calculator provides detailed insights into your RD journey.
      </Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🏦 How Recurring Deposits Work</Text>
        <Text style={styles.seoText}>
          Recurring Deposits are systematic investment plans where you deposit a fixed amount regularly 
          (monthly, quarterly, or annually) for a predetermined period. Unlike Fixed Deposits, RDs allow 
          you to build savings gradually while earning compound interest on each installment.
        </Text>
        
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>✅ RD Advantages</Text>
            <Text style={styles.comparisonText}>• Disciplined saving habit</Text>
            <Text style={styles.comparisonText}>• Guaranteed returns</Text>
            <Text style={styles.comparisonText}>• Flexible deposit frequency</Text>
            <Text style={styles.comparisonText}>• Lower minimum investment</Text>
            <Text style={styles.comparisonText}>• Deposit insurance coverage</Text>
          </View>
          
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>⚠️ RD Considerations</Text>
            <Text style={styles.comparisonText}>• Lower returns vs equity</Text>
            <Text style={styles.comparisonText}>• Penalty for missed deposits</Text>
            <Text style={styles.comparisonText}>• Taxable interest income</Text>
            <Text style={styles.comparisonText}>• Inflation impact on returns</Text>
            <Text style={styles.comparisonText}>• Limited liquidity options</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🚀 Key Calculator Features</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Bank-Specific Rates:</Text> Compare interest rates 
              from major banks including SBI, HDFC, ICICI, and Axis Bank with real-time rate updates.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Flexible Frequency:</Text> Calculate returns for 
              monthly, quarterly, half-yearly, or annual deposit frequencies.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Senior Citizen Benefits:</Text> Automatic calculation 
              of additional interest rates for senior citizens (typically 0.5% extra).
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Tax Planning:</Text> Comprehensive tax calculations 
              including TDS deduction and post-tax maturity amounts.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Visual Analytics:</Text> Interactive charts showing 
              deposit vs interest breakdown and growth trajectory.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Investment Comparison:</Text> Compare RD returns 
              with savings accounts, PPF, and mutual funds.
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📊 RD Interest Calculation Formula</Text>
        <Text style={styles.seoText}>
          RD maturity is calculated using compound interest on each installment:
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>M = P × [((1 + r/n)^(n×t) - 1) / (r/n)] × (1 + r/n)</Text>
        </Text>
        <Text style={styles.seoText}>
          Where: M = Maturity Amount, P = Monthly Deposit, r = Annual Interest Rate, 
          n = Compounding Frequency, t = Tenure in Years
        </Text>
        
        <View style={styles.taxRateTable}>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateHeader}>Bank</Text>
            <Text style={styles.taxRateHeader}>1 Year</Text>
            <Text style={styles.taxRateHeader}>2 Years</Text>
            <Text style={styles.taxRateHeader}>3 Years</Text>
            <Text style={styles.taxRateHeader}>5 Years</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>SBI</Text>
            <Text style={styles.taxRateCell}>6.0%</Text>
            <Text style={styles.taxRateCell}>6.25%</Text>
            <Text style={styles.taxRateCell}>6.5%</Text>
            <Text style={styles.taxRateCell}>6.25%</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>HDFC</Text>
            <Text style={styles.taxRateCell}>6.5%</Text>
            <Text style={styles.taxRateCell}>6.75%</Text>
            <Text style={styles.taxRateCell}>7.0%</Text>
            <Text style={styles.taxRateCell}>6.75%</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>ICICI</Text>
            <Text style={styles.taxRateCell}>6.25%</Text>
            <Text style={styles.taxRateCell}>6.5%</Text>
            <Text style={styles.taxRateCell}>6.75%</Text>
            <Text style={styles.taxRateCell}>6.5%</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📝 How to Use This RD Calculator</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>1</Text>
            </View>
            <Text style={styles.stepText}>
              Enter your desired deposit amount and select the frequency (monthly, quarterly, etc.)
            </Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>2</Text>
            </View>
            <Text style={styles.stepText}>
              Choose your preferred bank or enter a custom interest rate for comparison
            </Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>3</Text>
            </View>
            <Text style={styles.stepText}>
              Set the tenure period and indicate if you're a senior citizen for bonus rates
            </Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>4</Text>
            </View>
            <Text style={styles.stepText}>
              Enter your tax slab to calculate post-tax returns and TDS implications
            </Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>5</Text>
            </View>
            <Text style={styles.stepText}>
              Review detailed results including maturity amount, interest earned, and tax impact
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💡 RD Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Choose Optimal Tenure</Text>
              <Text style={styles.strategyText}>
                Select tenure based on your financial goals. Longer tenures typically offer higher 
                rates but consider inflation impact and liquidity needs.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Compare Bank Rates</Text>
              <Text style={styles.strategyText}>
                Different banks offer varying rates. Use our calculator to compare and choose 
                the bank offering the best returns for your tenure.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Senior Citizen Benefits</Text>
              <Text style={styles.strategyText}>
                If you're 60+, ensure you claim the additional 0.5% interest rate offered 
                by most banks for senior citizens.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Tax Planning</Text>
              <Text style={styles.strategyText}>
                Consider your tax slab when choosing RD tenure. Higher tax brackets may benefit 
                from tax-free alternatives like PPF for longer tenures.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Laddering Strategy</Text>
              <Text style={styles.strategyText}>
                Create multiple RDs with different maturity dates to ensure regular liquidity 
                and take advantage of changing interest rates.
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>⚖️ Legal & Compliance</Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Deposit Insurance:</Text> RDs are covered under 
          DICGC insurance up to ₹5 lakhs per depositor per bank, ensuring capital protection.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>TDS Regulations:</Text> Banks deduct 10% TDS if 
          annual interest exceeds ₹40,000 (₹50,000 for senior citizens). Provide PAN to avoid 
          higher TDS rates.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Premature Withdrawal:</Text> Most banks allow 
          premature closure after 6 months with penalty of 1-2% on applicable interest rate.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>❌ Common RD Mistakes to Avoid</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Missing Installments:</Text> Avoid penalties 
              by setting up auto-debit or maintaining sufficient balance.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Ignoring Tax Impact:</Text> Consider post-tax 
              returns when comparing with tax-free investments.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Not Comparing Banks:</Text> Interest rates vary 
              significantly between banks - always compare before investing.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>
              <Text style={{ fontWeight: 'bold' }}>Overlooking Inflation:</Text> Ensure RD returns 
              beat inflation to maintain purchasing power.
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📈 RD Success Examples</Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Example 1:</Text> ₹5,000 monthly RD for 5 years 
          at 6.5% = ₹3.5L deposits + ₹65,000 interest = ₹4.15L maturity
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Example 2:</Text> ₹10,000 quarterly RD for 3 years 
          at 7% = ₹1.2L deposits + ₹26,000 interest = ₹1.46L maturity
        </Text>
        <Text style={styles.seoText}>
          <Text style={{ fontWeight: 'bold' }}>Senior Citizen Benefit:</Text> Additional 0.5% can 
          increase returns by ₹8,000-15,000 on typical RD amounts over 3-5 years.
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>RD Details</Text>
      
      {/* Monthly Deposit Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>
          {depositFrequency === 'monthly' ? 'Monthly' : 
           depositFrequency === 'quarterly' ? 'Quarterly' :
           depositFrequency === 'half-yearly' ? 'Half-Yearly' : 'Yearly'} Deposit Amount
        </Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={monthlyDeposit}
            onChangeText={setMonthlyDeposit}
            placeholder="5000"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Deposit Frequency */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Deposit Frequency</Text>
        <View style={styles.segmentedControl}>
          {frequencyOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.segmentButton,
                depositFrequency === option.value && styles.segmentButtonActive,
              ]}
              onPress={() => setDepositFrequency(option.value)}
            >
              <Text style={[
                styles.segmentButtonText,
                depositFrequency === option.value && styles.segmentButtonTextActive,
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Bank Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Select Bank</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.bankScroll}>
          <View style={styles.bankContainer}>
            <TouchableOpacity
              style={[
                styles.bankCard,
                !selectedBank && styles.bankCardActive,
              ]}
              onPress={() => setSelectedBank(null)}
            >
              <Text style={[
                styles.bankName,
                !selectedBank && styles.bankNameActive,
              ]}>
                Custom Rate
              </Text>
              <Text style={[
                styles.bankRate,
                !selectedBank && styles.bankRateActive,
              ]}>
                Manual Entry
              </Text>
            </TouchableOpacity>
            {banks.map((bank) => (
              <TouchableOpacity
                key={bank.code}
                style={[
                  styles.bankCard,
                  selectedBank === bank.code && styles.bankCardActive,
                ]}
                onPress={() => setSelectedBank(bank.code)}
              >
                <Text style={[
                  styles.bankName,
                  selectedBank === bank.code && styles.bankNameActive,
                ]}>
                  {bank.name}
                </Text>
                <Text style={[
                  styles.bankRate,
                  selectedBank === bank.code && styles.bankRateActive,
                ]}>
                  Up to {Math.max(...Object.values(bank.rates))}%
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Interest Rate (if custom) */}
      {!selectedBank && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Interest Rate: {interestRate}%</Text>
          <Slider
            style={styles.slider}
            minimumValue={3}
            maximumValue={12}
            value={interestRate}
            onValueChange={setInterestRate}
            step={0.25}
            minimumTrackTintColor={colors.primary}
            maximumTrackTintColor={colors.border}
            thumbStyle={styles.sliderThumb}
          />
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderLabel}>3%</Text>
            <Text style={styles.sliderLabel}>12%</Text>
          </View>
        </View>
      )}

      {/* Tenure */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Tenure</Text>
        <View style={styles.tenureContainer}>
          <View style={styles.tenureInputContainer}>
            <TextInput
              style={styles.tenureInput}
              value={tenure.toString()}
              onChangeText={(value) => setTenure(parseFloat(value) || 0)}
              placeholder="12"
              keyboardType="numeric"
            />
          </View>
          <View style={styles.tenureTypeContainer}>
            {tenureOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.tenureTypeButton,
                  tenureType === option.value && styles.tenureTypeButtonActive,
                ]}
                onPress={() => setTenureType(option.value)}
              >
                <Text style={[
                  styles.tenureTypeText,
                  tenureType === option.value && styles.tenureTypeTextActive,
                ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Senior Citizen */}
      <View style={styles.inputGroup}>
        <TouchableOpacity
          style={styles.checkboxContainer}
          onPress={() => setIsSeniorCitizen(!isSeniorCitizen)}
        >
          <View style={[styles.checkbox, isSeniorCitizen && styles.checkboxActive]}>
            {isSeniorCitizen && (
              <Ionicons name="checkmark" size={16} color={colors.background} />
            )}
          </View>
          <Text style={styles.checkboxLabel}>Senior Citizen (Additional 0.5% interest)</Text>
        </TouchableOpacity>
      </View>

      {/* Tax Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Tax Rate: {taxRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={0}
          maximumValue={30}
          value={taxRate}
          onValueChange={setTaxRate}
          step={5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>0%</Text>
          <Text style={styles.sliderLabel}>30%</Text>
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { totalDeposits, interestEarned, maturityAmount } = results;
    const depositsPercentage = (totalDeposits / maturityAmount) * 100;
    const interestPercentage = (interestEarned / maturityAmount) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const depositsStrokeDasharray = `${(depositsPercentage / 100) * circumference} ${circumference}`;
    const interestStrokeDasharray = `${(interestPercentage / 100) * circumference} ${circumference}`;
    const interestStrokeDashoffset = -((depositsPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>RD Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Total deposits */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={depositsStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Interest earned */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={interestStrokeDasharray}
              strokeDashoffset={interestStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Maturity Amount
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(maturityAmount)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Total Deposits ({depositsPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Interest ({interestPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      monthlyAmount,
      totalDeposits, 
      maturityAmount, 
      interestEarned, 
      rate,
      effectiveRate,
      tdsAmount,
      taxOnInterest,
      postTaxInterest,
      postTaxMaturity,
      tenureInMonths,
      depositsPerYear
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>RD Results</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>
            {depositFrequency === 'monthly' ? 'Monthly' : 
             depositFrequency === 'quarterly' ? 'Quarterly' :
             depositFrequency === 'half-yearly' ? 'Half-Yearly' : 'Yearly'} Deposit
          </Text>
          <Text style={styles.resultValue}>{formatCurrency(monthlyAmount)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Interest Rate</Text>
          <Text style={styles.resultValue}>{rate.toFixed(2)}%</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Tenure</Text>
          <Text style={styles.resultValue}>{tenureInMonths} months</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Deposits</Text>
          <Text style={styles.resultValue}>{formatCurrency(totalDeposits)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Maturity Amount</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(maturityAmount)}
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Interest Earned</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(interestEarned)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Effective Rate</Text>
          <Text style={styles.resultValue}>{effectiveRate.toFixed(2)}%</Text>
        </View>

        {tdsAmount > 0 && (
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>TDS (10%)</Text>
            <Text style={[styles.resultValue, { color: colors.error }]}>
              {formatCurrency(tdsAmount)}
            </Text>
          </View>
        )}

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Tax on Interest</Text>
          <Text style={[styles.resultValue, { color: colors.error }]}>
            {formatCurrency(taxOnInterest)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Post-Tax Maturity</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(postTaxMaturity)}
          </Text>
        </View>
      </View>
    );
  };

  const renderGrowthChart = () => {
    if (!results || !results.monthlyBreakdown) return null;

    const { monthlyBreakdown, totalDeposits } = results;
    const maxAmount = Math.max(...monthlyBreakdown.map(item => item.maturity));
    const chartHeight = 200;
    const chartWidth = 300;
    const padding = 20;

    return (
      <View style={styles.growthCard}>
        <Text style={styles.cardTitle}>Growth Visualization</Text>
        <View style={styles.growthContainer}>
          <Svg width={chartWidth} height={chartHeight + padding * 2}>
            {/* Draw bars for deposits */}
            {monthlyBreakdown.slice(0, 12).map((item, index) => {
              const depositHeight = (item.deposits / maxAmount) * chartHeight;
              const interestHeight = (item.interest / maxAmount) * chartHeight;
              const barWidth = (chartWidth - padding * 2) / 12;
              const x = padding + index * barWidth;

              return (
                <React.Fragment key={index}>
                  {/* Deposits bar */}
                  <Rect
                    x={x}
                    y={chartHeight + padding - depositHeight}
                    width={barWidth - 2}
                    height={depositHeight}
                    fill={colors.primary}
                    opacity={0.8}
                  />
                  {/* Interest bar */}
                  <Rect
                    x={x}
                    y={chartHeight + padding - depositHeight - interestHeight}
                    width={barWidth - 2}
                    height={interestHeight}
                    fill={colors.success}
                    opacity={0.8}
                  />
                </React.Fragment>
              );
            })}
            
            {/* X-axis */}
            <Line
              x1={padding}
              y1={chartHeight + padding}
              x2={chartWidth - padding}
              y2={chartHeight + padding}
              stroke={colors.border}
              strokeWidth={1}
            />
            
            {/* Y-axis */}
            <Line
              x1={padding}
              y1={padding}
              x2={padding}
              y2={chartHeight + padding}
              stroke={colors.border}
              strokeWidth={1}
            />
          </Svg>
        </View>
        <Text style={styles.chartCaption}>Monthly Growth (First 12 months)</Text>
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Deposits</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Interest</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderComparison = () => {
    if (!results) return null;

    const { effectiveRate, postTaxMaturity, totalDeposits } = results;
    const tenureYears = results.tenureInMonths / 12;
    
    // Compare with other investment options
    const savingsAccount = totalDeposits * Math.pow(1.035, tenureYears); // 3.5% savings
    const ppf = totalDeposits * Math.pow(1.075, tenureYears); // 7.5% PPF
    const mutualFund = totalDeposits * Math.pow(1.12, tenureYears); // 12% mutual fund

    const comparisons = [
      { name: 'Recurring Deposit', value: postTaxMaturity, returns: effectiveRate, color: colors.primary },
      { name: 'Savings Account', value: savingsAccount, returns: 3.5, color: colors.warning },
      { name: 'PPF', value: ppf, returns: 7.5, color: colors.success },
      { name: 'Mutual Fund (Equity)', value: mutualFund, returns: 12, color: '#8B5CF6' },
    ];

    return (
      <View style={styles.comparisonCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowComparison(!showComparison)}
        >
          <Text style={styles.cardTitle}>Investment Comparison</Text>
          <Ionicons
            name={showComparison ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showComparison && (
          <View style={styles.cardContent}>
            {comparisons.map((item, index) => (
              <View key={index} style={styles.comparisonItem}>
                <View style={styles.comparisonHeader}>
                  <View style={[styles.comparisonIndicator, { backgroundColor: item.color }]} />
                  <Text style={styles.comparisonName}>{item.name}</Text>
                  <Text style={styles.comparisonReturns}>{item.returns.toFixed(1)}%</Text>
                </View>
                <Text style={styles.comparisonValue}>{formatCurrency(item.value)}</Text>
                <View style={styles.comparisonBar}>
                  <View 
                    style={[
                      styles.comparisonBarFill, 
                      { 
                        width: `${(item.value / Math.max(...comparisons.map(c => c.value))) * 100}%`,
                        backgroundColor: item.color 
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>About Recurring Deposits</Text>
      <Text style={styles.infoSubtitle}>Understanding RD Returns and Benefits</Text>
      
      <Text style={styles.infoText}>
        Recurring Deposits are systematic savings instruments where you deposit a fixed amount 
        regularly for a predetermined period. They offer guaranteed returns and help build 
        disciplined saving habits while providing better returns than savings accounts.
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Safety:</Text> RDs are covered under deposit 
          insurance up to ₹5 lakhs per depositor per bank, making them extremely safe investments.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="trending-up" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Flexibility:</Text> Most banks allow you to choose 
          deposit frequency (monthly, quarterly, etc.) and offer premature withdrawal options.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="calculator" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Tax Implications:</Text> Interest earned on RDs 
          is taxable as per your income tax slab. TDS is deducted if interest exceeds ₹40,000 per year.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is the minimum amount required to open an RD?",
        answer: "Most banks require a minimum monthly deposit of ₹100 to ₹500 to open a Recurring Deposit account. However, this varies by bank and account type."
      },
      {
        question: "What happens if I miss an RD installment?",
        answer: "Banks typically charge a penalty for missed installments, usually ₹1-5 per ₹100 of the installment amount. Some banks may close the account if multiple installments are missed."
      },
      {
        question: "Can I withdraw my RD before maturity?",
        answer: "Yes, premature withdrawal is allowed but banks charge a penalty of 1-2% on the applicable interest rate. The penalty varies by bank and the period for which the RD was maintained."
      },
      {
        question: "How is RD interest calculated?",
        answer: "RD interest is calculated using compound interest on the deposits made. Each installment earns interest from the date of deposit to the maturity date, compounded quarterly in most cases."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    segmentedControl: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
    },
    segmentButton: {
      flex: 1,
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 6,
      alignItems: 'center',
    },
    segmentButtonActive: {
      backgroundColor: colors.primary,
    },
    segmentButtonText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    segmentButtonTextActive: {
      color: colors.background,
    },
    bankScroll: {
      marginTop: 8,
    },
    bankContainer: {
      flexDirection: 'row',
      gap: 12,
      paddingRight: 20,
    },
    bankCard: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      minWidth: 140,
      borderWidth: 1,
      borderColor: colors.border,
    },
    bankCardActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    bankName: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    bankNameActive: {
      color: colors.primary,
    },
    bankRate: {
      fontSize: 10,
      color: colors.textSecondary,
    },
    bankRateActive: {
      color: colors.primary,
    },
    tenureContainer: {
      flexDirection: 'row',
      gap: 12,
      alignItems: 'center',
    },
    tenureInputContainer: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    tenureInput: {
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    tenureTypeContainer: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 2,
    },
    tenureTypeButton: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
    },
    tenureTypeButtonActive: {
      backgroundColor: colors.primary,
    },
    tenureTypeText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    tenureTypeTextActive: {
      color: colors.background,
    },
    checkboxContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderWidth: 2,
      borderColor: colors.border,
      borderRadius: 4,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    checkboxLabel: {
      fontSize: 14,
      color: colors.text,
    },
    slider: {
      height: 40,
      marginVertical: 10,
    },
    sliderThumb: {
      backgroundColor: colors.primary,
      width: 20,
      height: 20,
    },
    sliderLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    sliderLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    growthCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    growthContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartCaption: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 10,
      textAlign: 'center',
    },
    comparisonCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    comparisonItem: {
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    comparisonHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    comparisonIndicator: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginRight: 8,
    },
    comparisonName: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      flex: 1,
    },
    comparisonReturns: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    comparisonValue: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    comparisonBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
    },
    comparisonBarFill: {
      height: '100%',
      borderRadius: 2,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
    // SEO Content Styles
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    seoSubtitle: {
      fontSize: 18,
      color: colors.textSecondary,
      marginBottom: 20,
      textAlign: 'center',
    },
    seoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
      marginBottom: 16,
    },
    seoSection: {
      marginBottom: 24,
    },
    seoSectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    comparisonContainer: {
      gap: 16,
    },
    comparisonTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.primary,
      marginBottom: 8,
    },
    comparisonText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    featuresList: {
      gap: 8,
    },
    featureItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    featureText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    taxRateTable: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      overflow: 'hidden',
    },
    taxRateRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    taxRateHeader: {
      flex: 1,
      backgroundColor: colors.primary,
      color: colors.background,
      fontSize: 12,
      fontWeight: 'bold',
      padding: 12,
      textAlign: 'center',
    },
    taxRateCell: {
      flex: 1,
      backgroundColor: colors.background,
      fontSize: 12,
      color: colors.text,
      padding: 12,
      textAlign: 'center',
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    stepsList: {
      gap: 16,
    },
    stepItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    stepNumber: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
    },
    stepNumberText: {
      color: colors.background,
      fontSize: 14,
      fontWeight: 'bold',
    },
    stepText: {
      flex: 1,
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    strategiesList: {
      gap: 16,
    },
    strategyItem: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    strategyContent: {
      gap: 8,
    },
    strategyTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    strategyText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
          {renderGrowthChart()}
        </View>
      </View>
      
      {renderComparison()}
      {renderInformation()}
      {renderSEOContent()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};



export default RDCalculatorScreen;